import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, CheckCircle2, ChevronDown, Loader2, Mail, MapPin, Phone, Plus } from "lucide-react";
import { contact } from "@/data/site";
import useHashScroll from "@/lib/useHashScroll";
import { EASE, Eyebrow, Reveal } from "@/components/site/motion";

const helpOptions = [
  "Software & Product Development",
  "Web & Mobile Development",
  "AI, Data & Automation",
  "Cloud & Infrastructure",
  "Digital Transformation",
  "Technology Consulting",
  "General Enquiry",
  "Other",
];

const stageOptions = [
  "Idea / Planning",
  "Ready to Start",
  "Existing Product",
  "Need Improvement / Modernization",
  "Ongoing Technical Support",
  "Not Sure Yet",
];

const nextSteps = [
  { num: "01", title: "We Review", desc: "We review your enquiry, requirements, and the type of support you're looking for." },
  { num: "02", title: "We Connect", desc: "If there's a good fit, we'll get in touch to understand the project in more detail." },
  { num: "03", title: "We Define the Next Step", desc: "Depending on the project, that may be a discovery call, technical discussion, proposal, or recommended approach." },
];

const faqs = [
  { q: "Do I need a complete project specification?", a: "No. You can contact us with an idea, challenge, or rough requirement. We can help clarify the technical direction before development begins." },
  { q: "Can Bytevora work on an existing product?", a: "Yes. We can review existing systems, help modernize applications, integrate new features, improve infrastructure, or support ongoing development." },
  { q: "Can I contact you for a general business enquiry?", a: "Yes. Use the same form and select General Enquiry." },
  { q: "Do you offer ongoing support?", a: "Ongoing technical support and maintenance can be discussed depending on the project and service requirements." },
];

const inputClass =
  "h-[52px] w-full rounded-[10px] border border-slate-200 bg-white px-4 text-[15px] text-slate-900 outline-none transition-[border-color,box-shadow] duration-200 placeholder:text-slate-400 focus:border-blue-600 focus:ring-4 focus:ring-blue-600/10";
const labelClass = "mb-2 block text-[13px] font-medium text-slate-700";

function Field({ label, required, error, children, testId }) {
  return (
    <div data-testid={testId}>
      <label className={labelClass}>
        {label} {required && <span className="text-blue-600">*</span>}
      </label>
      {children}
      {error && <p className="mt-1.5 text-xs text-red-600">{error}</p>}
    </div>
  );
}

function SelectField({ value, onChange, options, placeholder, error, testId }) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={onChange}
        data-testid={testId}
        className={`${inputClass} appearance-none pr-10 ${value ? "" : "text-slate-400"} ${error ? "border-red-400" : ""}`}
      >
        <option value="" disabled>
          {placeholder}
        </option>
        {options.map((o) => (
          <option key={o} value={o} className="text-slate-900">
            {o}
          </option>
        ))}
      </select>
      <ChevronDown size={16} className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-slate-400" />
    </div>
  );
}

function EnquiryForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", help: "", stage: "", summary: "", consent: false, website: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  const set = (key) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.name.trim()) er.name = "Please enter your name.";
    if (!form.email.trim()) er.email = "Please enter your work email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = "Please enter a valid email address.";
    if (!form.help) er.help = "Please select what we can help with.";
    if (!form.summary.trim()) er.summary = "Please tell us a little about your project.";
    if (!form.consent) er.consent = "Please agree to the Privacy Policy to continue.";
    return er;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (form.website) return;
    const er = validate();
    if (Object.keys(er).length > 0) {
      setErrors(er);
      return;
    }
    setStatus("sending");
    window.setTimeout(() => setStatus("success"), 900);
  };

  if (status === "success") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: EASE }}
        className="flex h-full min-h-[420px] flex-col items-center justify-center text-center"
        data-testid="enquiry-success"
      >
        <span className="grid h-16 w-16 place-items-center rounded-full bg-blue-600/10 text-blue-600">
          <CheckCircle2 size={30} strokeWidth={1.8} />
        </span>
        <h3 className="mt-6 text-2xl font-bold tracking-tight text-slate-900">
          Thanks — we&rsquo;ve received your enquiry.
        </h3>
        <p className="mt-3 max-w-sm text-sm leading-relaxed text-slate-600">
          Our team will review the details and get back to you using the contact information you
          provided. If your enquiry is urgent, you can also reach us directly at{" "}
          <a href={`mailto:${contact.email}`} className="font-medium text-blue-600">{contact.email}</a>.
        </p>
        <button
          type="button"
          data-testid="enquiry-send-another"
          onClick={() => {
            setForm({ name: "", email: "", phone: "", company: "", help: "", stage: "", summary: "", consent: false, website: "" });
            setStatus("idle");
          }}
          className="mt-7 text-sm font-semibold text-slate-900 underline decoration-slate-300 underline-offset-4 transition-colors hover:text-blue-600 hover:decoration-blue-600"
        >
          Send another enquiry
        </button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate data-testid="enquiry-form">
      <input
        type="text"
        tabIndex={-1}
        autoComplete="off"
        value={form.website}
        onChange={set("website")}
        className="hidden"
        aria-hidden="true"
      />
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Full Name" required error={errors.name} testId="field-name">
          <input type="text" value={form.name} onChange={set("name")} placeholder="Your name" data-testid="input-name" className={`${inputClass} ${errors.name ? "border-red-400" : ""}`} />
        </Field>
        <Field label="Work Email" required error={errors.email} testId="field-email">
          <input type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" data-testid="input-email" className={`${inputClass} ${errors.email ? "border-red-400" : ""}`} />
        </Field>
        <Field label="Phone Number" testId="field-phone">
          <input type="tel" value={form.phone} onChange={set("phone")} placeholder="+91 ..." data-testid="input-phone" className={inputClass} />
        </Field>
        <Field label="Company / Organization" testId="field-company">
          <input type="text" value={form.company} onChange={set("company")} placeholder="Company name" data-testid="input-company" className={inputClass} />
        </Field>
        <Field label="What can we help with?" required error={errors.help} testId="field-help">
          <SelectField value={form.help} onChange={set("help")} options={helpOptions} placeholder="Select a service" error={errors.help} testId="select-help" />
        </Field>
        <Field label="Project Stage" testId="field-stage">
          <SelectField value={form.stage} onChange={set("stage")} options={stageOptions} placeholder="Select a stage" testId="select-stage" />
        </Field>
      </div>
      <div className="mt-5" data-testid="field-summary">
        <label className={labelClass}>
          Tell us about your project <span className="text-blue-600">*</span>
        </label>
        <textarea
          value={form.summary}
          onChange={set("summary")}
          placeholder="What are you looking to build, improve, or solve?"
          data-testid="input-summary"
          className={`min-h-[160px] w-full resize-y rounded-[10px] border border-slate-200 bg-white px-4 py-3.5 text-[15px] text-slate-900 outline-none transition-[border-color,box-shadow] duration-200 placeholder:text-slate-400 focus:border-blue-600 focus:ring-4 focus:ring-blue-600/10 ${errors.summary ? "border-red-400" : ""}`}
        />
        {errors.summary && <p className="mt-1.5 text-xs text-red-600">{errors.summary}</p>}
      </div>
      <div className="mt-6" data-testid="field-consent">
        <label className="flex cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            checked={form.consent}
            onChange={set("consent")}
            data-testid="input-consent"
            className="mt-0.5 h-[18px] w-[18px] shrink-0 cursor-pointer rounded border-slate-300 accent-blue-600"
          />
          <span className="text-[13px] leading-relaxed text-slate-600">
            I agree to the{" "}
            <Link to="/privacy-policy" className="font-medium text-blue-600 underline decoration-blue-600/30 underline-offset-4 hover:decoration-blue-600">
              Privacy Policy
            </Link>{" "}
            and consent to Bytevora contacting me regarding this enquiry.
          </span>
        </label>
        {errors.consent && <p className="mt-1.5 text-xs text-red-600">{errors.consent}</p>}
      </div>
      <button
        type="submit"
        data-testid="enquiry-submit-button"
        disabled={status === "sending"}
        className="group mt-7 inline-flex w-full items-center justify-center gap-2.5 rounded-[10px] bg-navy px-7 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-blue-600 disabled:opacity-70 sm:w-auto"
      >
        {status === "sending" ? (
          <>
            <Loader2 size={16} className="animate-spin" />
            Submitting…
          </>
        ) : (
          <>
            Submit Enquiry
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </>
        )}
      </button>
    </form>
  );
}

function FaqItem({ item, open, onToggle, index }) {
  return (
    <div className="border-b border-slate-200 last:border-0">
      <button
        type="button"
        data-testid={`faq-question-${index}`}
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-6 py-6 text-left"
      >
        <span className="text-[15.5px] font-semibold tracking-tight text-slate-900">{item.q}</span>
        <Plus size={17} className={`shrink-0 text-slate-400 transition-transform duration-300 ${open ? "rotate-45" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="overflow-hidden"
          >
            <p className="max-w-2xl pb-6 text-sm leading-[1.8] text-slate-600" data-testid={`faq-answer-${index}`}>
              {item.a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function ContactPage() {
  useHashScroll();
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <main className="bg-white" data-testid="contact-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-100 pt-[72px]">
        <div className="bg-grid-light absolute inset-0" aria-hidden="true" />
        <div className="absolute -right-40 top-[-140px] h-[400px] w-[400px] rounded-full bg-blue-600/10 blur-[120px]" aria-hidden="true" />
        <div className="relative mx-auto w-full max-w-[1200px] px-6 py-20 md:px-8 md:py-24">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }}>
            <Eyebrow>Contact Bytevora</Eyebrow>
          </motion.div>
          <motion.h1
            data-testid="contact-hero-heading"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.95, ease: EASE, delay: 0.12 }}
            className="mt-6 max-w-[760px] text-[clamp(2.5rem,5vw,4.2rem)] font-extrabold leading-[1.03] tracking-[-0.03em] text-slate-900"
          >
            Let&rsquo;s talk about{" "}
            <span className="bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent">
              what you&rsquo;re building.
            </span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.28 }}
            className="mt-6 max-w-[560px] text-base leading-relaxed text-slate-600 md:text-lg"
          >
            Whether you&rsquo;re planning a new digital product, modernizing an existing system,
            exploring AI or automation, or simply need technical guidance, tell us a little about
            what you need.
          </motion.p>
        </div>
      </section>

      {/* Main contact */}
      <section id="project-enquiry" className="scroll-mt-20 bg-[#F8FAFC] py-24 md:py-28" data-testid="contact-main">
        <div className="mx-auto grid w-full max-w-[1200px] gap-12 px-6 md:px-8 lg:grid-cols-[1.15fr_0.85fr]">
          <Reveal>
            <div className="rounded-[24px] border border-slate-200 bg-white p-8 shadow-[0_30px_60px_-45px_rgba(11,18,32,0.25)] md:p-10">
              <h2 className="text-2xl font-bold tracking-tight text-slate-900">Start a conversation</h2>
              <p className="mt-2.5 text-sm leading-relaxed text-slate-500">
                Share a few details about your project or enquiry. You don&rsquo;t need to have
                everything figured out yet.
              </p>
              <div className="mt-8">
                <EnquiryForm />
              </div>
            </div>
          </Reveal>

          <div className="flex flex-col gap-10">
            <Reveal delay={1}>
              <h2 className="text-xl font-bold tracking-tight text-slate-900">Prefer to reach us directly?</h2>
              <div className="mt-6 divide-y divide-slate-200 border-y border-slate-200">
                <a href={`mailto:${contact.email}`} data-testid="contact-email-row" className="group flex items-center gap-4 py-5">
                  <span className="grid h-10 w-10 place-items-center rounded-xl border border-slate-200 bg-white text-blue-600">
                    <Mail size={16} strokeWidth={1.8} />
                  </span>
                  <span>
                    <span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">Email</span>
                    <span className="mt-1 block text-[15px] font-semibold text-slate-900 transition-colors group-hover:text-blue-600">{contact.email}</span>
                  </span>
                </a>
                <a href={contact.phoneHref} data-testid="contact-phone-row" className="group flex items-center gap-4 py-5">
                  <span className="grid h-10 w-10 place-items-center rounded-xl border border-slate-200 bg-white text-blue-600">
                    <Phone size={16} strokeWidth={1.8} />
                  </span>
                  <span>
                    <span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">Phone</span>
                    <span className="mt-1 block text-[15px] font-semibold text-slate-900 transition-colors group-hover:text-blue-600">{contact.phone}</span>
                  </span>
                </a>
                <div className="flex items-center gap-4 py-5" data-testid="contact-location-row">
                  <span className="grid h-10 w-10 place-items-center rounded-xl border border-slate-200 bg-white text-blue-600">
                    <MapPin size={16} strokeWidth={1.8} />
                  </span>
                  <span>
                    <span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">Location</span>
                    <span className="mt-1 block text-[15px] font-semibold text-slate-900">{contact.location}</span>
                  </span>
                </div>
              </div>
            </Reveal>

            <Reveal delay={2}>
              <div className="rounded-[20px] border border-slate-200 bg-white p-7" data-testid="contact-company-details">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">Company Details</p>
                <p className="mt-3.5 text-[15px] font-semibold text-slate-900">{contact.legalName}</p>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">
                  <span className="font-medium text-slate-900">CIN:</span>{" "}
                  <span className="font-mono text-[13px]">{contact.cin}</span>
                </p>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  <span className="font-medium text-slate-900">GSTIN:</span>{" "}
                  <span className="font-mono text-[13px]">{contact.gst}</span>
                </p>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  <span className="font-medium text-slate-900">Registered Office:</span>
                  <br />
                  {contact.registeredOffice}
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* What Happens Next */}
      <section className="bg-white py-24 md:py-28" data-testid="contact-next-steps">
        <div className="mx-auto w-full max-w-[1200px] px-6 md:px-8">
          <Reveal className="max-w-xl">
            <Eyebrow>What happens next</Eyebrow>
            <h2 className="mt-5 text-3xl font-bold leading-[1.08] tracking-[-0.02em] text-slate-900 md:text-[2.4rem]">
              A simple next step.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-10 md:grid-cols-3 md:gap-8">
            {nextSteps.map((s, i) => (
              <Reveal key={s.num} delay={i} className="group relative border-t-2 border-slate-100 pt-8 transition-colors duration-300 hover:border-blue-600">
                <span className="font-mono text-sm tracking-[0.16em] text-slate-300 transition-colors duration-300 group-hover:text-blue-600">
                  /{s.num}
                </span>
                <h3 className="mt-4 text-lg font-semibold tracking-tight text-slate-900">{s.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{s.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#F8FAFC] py-24 md:py-28" data-testid="contact-faq">
        <div className="mx-auto grid w-full max-w-[1200px] gap-12 px-6 md:px-8 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <Eyebrow>Practical questions</Eyebrow>
            <h2 className="mt-5 text-3xl font-bold leading-[1.08] tracking-[-0.02em] text-slate-900 md:text-[2.4rem]">
              Before you reach out.
            </h2>
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-slate-600 md:text-base">
              Can&rsquo;t find the answer here? Send us a message at{" "}
              <a href={`mailto:${contact.email}`} className="font-medium text-blue-600">{contact.email}</a>.
            </p>
          </Reveal>
          <Reveal delay={1} className="lg:col-span-7">
            <div className="rounded-[20px] border border-slate-200 bg-white px-7 md:px-9">
              {faqs.map((f, i) => (
                <FaqItem key={f.q} item={f} index={i} open={openFaq === i} onToggle={() => setOpenFaq(openFaq === i ? -1 : i)} />
              ))}
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
